/**
 * Design Systems for AI checklist app.
 *
 * Flow: Hero → Checklist (15 items) → sticky nav "View results" → loading → Results page
 * (tier card, email capture, What's next?) → optional email submit → Results sent page.
 *
 * Supports ?preview=results for a full results preview without completing the quiz.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Confetti from 'react-confetti';

import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { ChecklistSection } from './components/ChecklistSection';
import { ScoreResults } from './components/ScoreResults';
import { HelpBlock } from './components/HelpBlock';
import { SectionNav } from './components/SectionNav';
import { ResultsPreviewPage } from './components/ResultsPreviewPage';
import { Footer } from './components/Footer';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ShareDialog } from './components/ShareDialog';
import { Button } from './components/ui/button';

import type { ChecklistState, ChecklistSection as ChecklistSectionType, ResultsGuide } from './types';
import { checklistContent } from './data/checklistContent';
import { parseResultsGuide } from './utils/markdownParser';
import { calculateScore, getTierByScore } from './utils/scoreCalculator';
import { saveExpandedState, getExpandedStateFromChecklist } from './utils/localStorage';
import { SCORE_CONFIG, SOCIAL_SHARE, UI_MESSAGES, HEADER_CONFIG } from './constants';
import { useToast } from './components/ui/toast';

function App() {
  // ——— Content & checklist state ———
  const [checklistState, setChecklistState] = useState<ChecklistState>({});
  const [hero, setHero] = useState<string>('');
  const [sections, setSections] = useState<ChecklistSectionType[]>([]);
  const [resultsGuide, setResultsGuide] = useState<ResultsGuide | null>(null);
  // ——— UI state (modals, pages, loading) ———
  const [showConfetti, setShowConfetti] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const [showResultsPage, setShowResultsPage] = useState(false);
  const [viewResultsLoading, setViewResultsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Ref used in expanded-state cleanup to read latest state after unmount. */
  const checklistStateRef = useRef<ChecklistState>({});
  const { addToast } = useToast();

  /** Load checklist content and results guide (markdown), then init empty checklist state. */
  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        const [resultsModule] = await Promise.all([
          import('./data/scoring-results.md'),
        ]);
        const resultsText = resultsModule.default;
        const parsedGuide = parseResultsGuide(resultsText);

        if (!isMounted) return;

        setHero(checklistContent.hero);
        setSections(checklistContent.sections);
        setResultsGuide(parsedGuide);

        const initialState: ChecklistState = {};

        checklistContent.sections.forEach((section) => {
          section.items.forEach((item) => {
            initialState[item.id] = {
              rating: null,
              expanded: true,
              timestamp: null,
            };
          });
        });

        setChecklistState(initialState);
      } catch (err) {
        if (isMounted) {
          const message = err instanceof Error ? err.message : UI_MESSAGES.loadError;
          setLoadError(message);
          addToast(UI_MESSAGES.loadError, 'error');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();
  }, [addToast]);

  /** Keep ref in sync so cleanup can save latest expanded state. */
  useEffect(() => {
    checklistStateRef.current = checklistState;
  }, [checklistState]);

  /** Persist expanded/collapsed state per item (debounced); on cleanup, save immediately. */
  useEffect(() => {
    if (Object.keys(checklistState).length === 0) return;
    saveExpandedState(getExpandedStateFromChecklist(checklistState));
    return () => {
      const latestState = checklistStateRef.current;
      if (Object.keys(latestState).length > 0) {
        saveExpandedState(getExpandedStateFromChecklist(latestState), true);
      }
    };
  }, [checklistState]);

  const score = calculateScore(checklistState, sections);

  /** Show confetti when user has rated all items and has a valid score. */
  useEffect(() => {
    if (
      score.rated === SCORE_CONFIG.perfectScore.rated &&
      score.total >= SCORE_CONFIG.perfectScore.minTotal &&
      score.maxScore > 0
    ) {
      setShowConfetti(true);
      const timeoutId = setTimeout(() => setShowConfetti(false), SCORE_CONFIG.confettiDuration);
      return () => clearTimeout(timeoutId);
    }
  }, [score.total, score.maxScore, score.rated]);

  // ——— Handlers ———
  const handleRate = useCallback((itemId: string, rating: number) => {
    if (typeof itemId !== 'string' || !itemId.trim()) return;
    if (typeof rating !== 'number' || !Number.isInteger(rating) || rating < 0 || rating > 3) return;
    setChecklistState((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        rating,
        timestamp: Date.now(),
      },
    }));
  }, []);

  const handleToggleExpand = useCallback((itemId: string) => {
    if (typeof itemId !== 'string' || !itemId.trim()) return;
    setChecklistState((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        expanded: !prev[itemId]?.expanded,
      },
    }));
  }, []);

  /** Smooth-scroll to a section by ID (accounts for fixed header). */
  const scrollToSection = useCallback((sectionId: string) => {
    if (typeof sectionId !== 'string' || !sectionId.trim()) return;
    const element = document.getElementById(sectionId);
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset + HEADER_CONFIG.scrollOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      addToast(UI_MESSAGES.linkCopied, 'success');
    } catch {
      addToast('Failed to copy link', 'error');
    }
  }, [addToast]);

  /** Triggered by sticky nav "View results": show loading, then switch to results page. */
  const viewResultsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleViewResults = useCallback(() => {
    setViewResultsLoading(true);
    if (viewResultsTimeoutRef.current) {
      clearTimeout(viewResultsTimeoutRef.current);
    }
    viewResultsTimeoutRef.current = setTimeout(() => {
      setViewResultsLoading(false);
      setShowResultsPage(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 700);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (viewResultsTimeoutRef.current) {
        clearTimeout(viewResultsTimeoutRef.current);
      }
    };
  }, []);

  const handleBackToChecklist = useCallback(() => {
    setShowResultsPage(false);
    scrollToSection('section-1');
  }, [scrollToSection]);

  // ——— Memoized values (must be before early returns to follow Rules of Hooks) ———
  /** Map item number → { id, title } for section nav and deep links. */
  const allItemsMap = useMemo(() => {
    const map = new Map<number, { id: string; title: string }>();
    sections.forEach((s) => {
      s.items.forEach((item) => {
        map.set(item.number, { id: item.id, title: item.title });
      });
    });
    return map;
  }, [sections]);

  // ——— Share dialog content (tool vs results) ———
  const shareToolText = SOCIAL_SHARE.twitter.getShareToolText();
  const tierLevel = getTierByScore(score.total);
  const tierName = resultsGuide?.tiers.find((t) => t.level === tierLevel)?.name;
  const shareResultsText = SOCIAL_SHARE.twitter.getShareResultsText(score.total, score.maxScore, tierName);
  /** Props for the "Share Your Results" dialog (used on results page and results-sent page). */
  const shareResultsDialogProps = {
    open: showShareModal,
    onOpenChange: setShowShareModal,
    shareText: shareResultsText,
    title: 'Share Your Results',
    description: (
      <>
        Your score: <span className="font-semibold text-primary">{score.total}/{score.maxScore}</span> ({score.rated}/{SCORE_CONFIG.perfectScore.rated} items rated)
      </>
    ),
    onCopyLink: handleCopyLink,
  };

  // ——— Early returns (loading, error, preview, alternate pages) ———
  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading checklist..." />;
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-6">
        <div className="max-w-md w-full bg-white rounded-lg border border-border p-8 text-center">
          <div className="mb-6">
            <svg
              className="mx-auto h-12 w-12 text-danger"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-text-primary mb-2">
            Failed to load checklist
          </h1>
          <p className="text-text-secondary mb-6">{loadError}</p>
          <Button onClick={() => window.location.reload()} size="default">
            Reload Page
          </Button>
        </div>
      </div>
    );
  }

  /** ?preview=results shows all tiers and email capture without completing the quiz. */
  const isPreview =
    typeof window !== 'undefined' &&
    window.location.search.includes('preview=results') &&
    !previewDismissed;

  const handlePreviewBack = () => {
    setPreviewDismissed(true);
    window.history.replaceState({}, '', window.location.pathname);
  };

  if (isPreview) {
    return (
      <ResultsPreviewPage
        guide={resultsGuide}
        onBack={handlePreviewBack}
      />
    );
  }

  if (viewResultsLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner fullScreen message="Loading your results..." />
      </div>
    );
  }

  if (showResultsPage && score.rated >= SCORE_CONFIG.perfectScore.rated) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header score={score} onShare={() => setShowShareModal(true)} shareContext="results" />
        <main id="main" className="flex-1 flex flex-col px-6 pt-12" role="main">
          <div className="max-w-container mx-auto w-full">
            <div className="mb-8">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToChecklist}
                className="text-text-secondary hover:text-text-primary"
                aria-label="Back to checklist"
              >
                ← Back to checklist
              </Button>
            </div>
            <ScoreResults
              score={score}
              resultsGuide={resultsGuide}
              onShareClick={() => setShowShareModal(true)}
            />
          </div>
          {resultsGuide && (
            <div className="mt-auto">
              <HelpBlock content={resultsGuide.needHelp} asFauxFooter />
            </div>
          )}
        </main>
        <Footer variant="results" />
        <ShareDialog {...shareResultsDialogProps} />
      </div>
    );
  }

  // ——— Main checklist view ———
  return (
    <div className="min-h-screen bg-white">
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={500}
        />
      )}

      <div className="bg-gradient-to-b from-blue-50/40 to-white">
        <Header score={score} onShare={() => setShowShareModal(true)} shareContext="tool" />
        <Hero content={hero} onStartAssessment={() => scrollToSection('section-1')} />
      </div>

      <main id="main" className="px-6 py-12 pb-24" role="main">
        <div className="max-w-container mx-auto">
          {sections.map((section) => (
            <div key={section.id}>
              <ChecklistSection
                section={section}
                state={checklistState}
                onRate={handleRate}
                onToggleExpand={handleToggleExpand}
                allItemsMap={allItemsMap}
              />
            </div>
          ))}

          {score.rated >= SCORE_CONFIG.perfectScore.rated ? null : (
            <ScoreResults score={score} resultsGuide={resultsGuide} />
          )}
        </div>

        {/* "What happens next" only appears on the results page (after View results) */}
      </main>

      {sections.length > 0 && (
        <SectionNav
          sections={sections}
          state={checklistState}
          onViewResults={handleViewResults}
        />
      )}

      <Footer variant="quiz" />

      <ShareDialog
        open={showShareModal}
        onOpenChange={setShowShareModal}
        shareText={shareToolText}
        title="Share this checklist"
        description={
          <span>
            Audit your design system for AI-readiness with this 15-item checklist.
          </span>
        }
        onCopyLink={handleCopyLink}
      />
    </div>
  );
}

export default App;
